import { FunctionComponent, useState, useEffect } from 'react';
import { Box, FormControl, FormLabel, HStack, Radio, RadioGroup } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { RangeIntegerInput } from './AttributeFormControl/components/RangeIntegerInput';
import { AdComponentProps } from '@/pages/Ad/types';

const workloadSteps = Array.from({ length: 10 }, (_, i) => (i + 1) * 10);
const fulltimeValue = '{"min":"100","max":"100"}';
const defaultParttimeValue = `{"min":"${workloadSteps[0]}","max":"${workloadSteps[workloadSteps.length - 1]}" }`;

export const WorkloadSelector: FunctionComponent<AdComponentProps> = ({ attribute }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { formData } = useAppSelector((state) => state.ad);
  const storedValue = formData[attribute ? attribute.id : '']?.value;
  const isStoredParttime = !!storedValue && storedValue !== fulltimeValue;
  const [workloadType, setWorkloadType] = useState<string>(isStoredParttime ? 'parttime' : 'fulltime');
  const [savedParttimeValue, setSavedParttimeValue] = useState<string>(
    isStoredParttime ? storedValue : defaultParttimeValue
  );

  const handleTypeChange = (type: string) => {
    setWorkloadType(type);
    if (type === 'fulltime') {
      const current = formData[attribute ? attribute.id : '']?.value;
      if (current && current !== fulltimeValue) {
        setSavedParttimeValue(current);
      }
      dispatch(adActions.updateFormData({ key: attribute ? attribute.id : '', value: fulltimeValue }));
    } else {
      dispatch(adActions.updateFormData({ key: attribute ? attribute.id : '', value: savedParttimeValue }));
    }
  };

  useEffect(() => {
    if (!attribute) return;
    if (storedValue) return;

    dispatch(
      adActions.updateFormData({
        key: attribute.id,
        value: fulltimeValue,
      })
    );
  }, [attribute, storedValue, dispatch]);

  if (!attribute) return null;

  return (
    <FormControl isRequired={attribute.mandatory} my={2}>
      <FormLabel>{attribute.name}</FormLabel>
      <Box pl={1} pr={1}>
        <RadioGroup value={workloadType} onChange={handleTypeChange} my={4}>
          <HStack spacing={4}>
            <Radio value="fulltime">{t('ad.steps.attributes.workload.fulltime')}</Radio>
            <Radio value="parttime">{t('ad.steps.attributes.workload.parttime')}</Radio>
          </HStack>
        </RadioGroup>
        {workloadType === 'parttime' && <RangeIntegerInput attribute={attribute} allowedValues={workloadSteps} />}
      </Box>
    </FormControl>
  );
};
